import {
  CellMeta,
  ExternalForecastCt,
  ExternalForecastEditRequest,
  ExternalForecastField,
  ExternalForecastPeriod,
  ExternalForecastPeriodEdit,
  ExternalForecastYear
} from "./external-forecast.model";
import {truncateDecimals} from "./external-forecast.utils";

// Pure helpers for the edit call (phase-2-edit-discard spec sections 4 and 9.1).

export type EditableField = "businessMetric" | "contactRate" | "ccVolumePct" | "aiMix" | "aiVolume";

export interface CellChange {
  period: string | null;        // null = Total column
  field?: EditableField;        // exactly one of field / ctOid; ctOid changes are month-only
  ctOid?: string;
  value: number | null;
  fromPaste?: boolean;          // written by a clipboard paste (ag-grid source "paste"); set only when true
}

const CLEAR_KEY: Record<Exclude<EditableField, "aiMix" | "aiVolume">, ExternalForecastField> = {
  businessMetric: "BUSINESS_METRIC",
  contactRate: "CONTACT_RATE",
  ccVolumePct: "CC_VOLUME_PCT"
};

export function isValidValue(value: number | null, meta: CellMeta | null | undefined): boolean {
  if (!meta || meta.status !== "EDITED") {
    return false;
  }
  if (value === null) {
    return meta.allowBlank;
  }
  if (!Number.isFinite(value)) {
    // a bare "-" committed in the editor, or a pasted value that is not a number; also +/-Infinity (a paste of 1e309),
    // which passes an unbounded field but is serialized as null, so the sent edit would not be the accepted input
    return false;
  }
  return (meta.min === null || value >= meta.min) && (meta.max === null || value <= meta.max);
}

/** An open CT of a month: its share is editable, counts toward the 100.00 total and is sent in the split. */
export function isOpenCt(ct: ExternalForecastCt): boolean {
  return ct.pctMeta?.status === "EDITED";
}

/** The month's open-CT shares, each truncated to 2 decimals as it is sent, summed in hundredths; a blank share counts as 0 (and is sent as 0). */
export function ctMonthTotal(period: ExternalForecastPeriod, pctOf: (ctOid: string) => number | null): number {
  const hundredths = period.cts
    .filter(isOpenCt)
    .reduce((acc, ct) => acc + Math.round(truncateDecimals(pctOf(ct.ctOid) ?? 0) * 100), 0);
  return hundredths / 100;
}

export function isCtMonthValid(total: number): boolean {
  return Math.round(total * 100) === 10000;
}

/**
 * Balance of a month whose open-CT shares do not total 100.00: the CTs not in {@code fixed} take what the fixed ones leave, in
 * proportion to their current share (equally when all are 0), 2 decimals, the remainder on the last of them; as the lib's
 * CtDistributionBalancer.rebalance. Null when there is nothing to scale, a changed share is blank or not a number, or the fixed shares
 * already total 100 or more.
 * @param shares the month's open CTs in display order
 * @param fixed the CTs the planner changed, which keep their share
 * @return the new share of every CT that is not fixed
 */
export function balanceCtMonth(shares: {ctOid: string; value: number | null}[], fixed: ReadonlySet<string>): Record<string, number> | null {
  const others = shares.filter(share => !fixed.has(share.ctOid));
  const changed = shares.filter(share => fixed.has(share.ctOid));
  if (changed.some(share => share.value === null || !Number.isFinite(share.value))) {
    return null;   // a changed cell that is blank or not a number must be corrected first
  }
  const fixedHundredths = changed.reduce((acc, share) => acc + Math.round(truncateDecimals(share.value as number) * 100), 0);
  const target = 10000 - fixedHundredths;
  if (others.length === 0 || target <= 0) {
    return null;
  }
  const weights = others.map(share => Math.max(share.value ?? 0, 0));
  const weightSum = weights.reduce((acc, weight) => acc + weight, 0);
  const hundredths = others.map((_, i) => i === others.length - 1 ? 0
    : Math.round((weightSum <= 0 ? 1 / others.length : weights[i] / weightSum) * target));
  const last = target - hundredths.reduce((acc, value) => acc + value, 0);
  if (last < 0) {
    // rounding pushed the others over the target: the excess comes off the largest others, none going below zero
    let excess = -last;
    while (excess > 0) {
      const largest = hundredths.indexOf(Math.max(...hundredths));
      const deduction = Math.min(hundredths[largest], excess);
      hundredths[largest] -= deduction;
      excess -= deduction;
    }
    hundredths[others.length - 1] = 0;
  } else {
    hundredths[others.length - 1] = last;
  }

  const result: Record<string, number> = {};
  others.forEach((share, i) => result[share.ctOid] = hundredths[i] / 100);
  return result;
}

function addClear(edit: ExternalForecastPeriodEdit, field: ExternalForecastField): void {
  edit.clear = edit.clear ?? [];
  if (!edit.clear.includes(field)) {
    edit.clear.push(field);
  }
}

export function buildEditRequest(
  changes: CellChange[],
  year: ExternalForecastYear,
  pctOf: (period: string, ctOid: string) => number | null
): ExternalForecastEditRequest | null {
  const request: ExternalForecastEditRequest = {};
  const periods: Record<string, ExternalForecastPeriodEdit> = {};
  const editOf = (period: string) => periods[period] ?? (periods[period] = {});

  for (const change of changes) {
    if (change.period === null) {
      if (change.field !== "businessMetric") {
        continue;
      }
      if (change.value !== null) {
        request.annualBusinessMetric = change.value;
      } else {
        // a month whose own Business Metric changes in this batch is sent as that change, never also as a clear
        year.periods
          .filter(p => p.cellMeta.BUSINESS_METRIC?.status === "EDITED" && p.businessMetric !== null)
          .filter(p => !changes.some(c => c.period === p.period && c.field === "businessMetric"))
          .forEach(p => addClear(editOf(p.period), "BUSINESS_METRIC"));
      }
      continue;
    }
    const edit = editOf(change.period);
    if (change.ctOid !== undefined) {
      const month = year.periods.find(p => p.period === change.period);
      const split: Record<string, number> = {};
      for (const ct of month?.cts ?? []) {
        if (!isOpenCt(ct)) {
          continue;
        }
        // a blank share is sent as 0, as ctMonthTotal counted it
        split[ct.ctOid] = truncateDecimals((ct.ctOid === change.ctOid ? change.value : pctOf(change.period, ct.ctOid)) ?? 0);
      }
      edit.ctDistributions = split;
    } else if (change.field === "aiMix" || change.field === "aiVolume") {
      // both AI cells of a month in one request: AI Volume wins
      const volumeChanged = changes.some(c => c.period === change.period && c.field === "aiVolume");
      if (change.field === "aiMix" && volumeChanged) {
        continue;
      }
      if (change.value === null) {
        addClear(edit, "AI");
      } else if (change.field === "aiMix") {
        edit.aiType = "PERCENTAGE";
        edit.aiMix = change.value;
      } else {
        edit.aiType = "VOLUME";
        edit.aiVolume = change.value;
      }
    } else if (change.field) {
      if (change.value === null) {
        addClear(edit, CLEAR_KEY[change.field]);
      } else {
        edit[change.field] = change.value;
      }
    }
  }

  const nonEmpty = Object.entries(periods).filter(([, edit]) => Object.keys(edit).length > 0);
  if (nonEmpty.length > 0) {
    request.periods = Object.fromEntries(nonEmpty);
  }
  return Object.keys(request).length > 0 ? request : null;
}
